import React, { useState, useEffect } from "react";
import { apiJson, api, API_BASE } from "../../utils/api";

const DocumentUploadPage = () => {
  const [requirements, setRequirements] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [lockedDocTypeIds, setLockedDocTypeIds] = useState([]);
  const [admission, setAdmission] = useState(null);
  const [aadhaar, setAadhaar] = useState("");
  const [aadhaarLocked, setAadhaarLocked] = useState(false);
  const [savingAadhaar, setSavingAadhaar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const admData = await apiJson("/api/student/admission");
      setAdmission(admData.admission);
      setAadhaar(admData.student?.aadhaar || "");
      setAadhaarLocked(admData.admission?.status === "ADMITTED");

      if (admData.admission) {
        const reqs = await apiJson("/api/student/admission/documents");
        setRequirements(reqs.requirements || []);
        setUploads(reqs.uploads || []);
        setLockedDocTypeIds(reqs.lockedDocTypeIds || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (documentTypeId, file) => {
    setError("");
    setMessage("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentTypeId", documentTypeId);

    try {
      const res = await api("/api/student/admission/documents", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setMessage("Document uploaded successfully!");
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveAadhaar = async () => {
    setSavingAadhaar(true);
    setError("");
    try {
      await apiJson("/api/student/admission/form", {
        method: "PUT",
        body: JSON.stringify({ isDraft: true, personalInfo: { aadhaar } }),
      });
      setMessage("Aadhaar number saved");
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingAadhaar(false);
    }
  };

  if (loading) return <p className="text-gray-500">Loading...</p>;

  if (!admission) {
    return (
      <div className="max-w-2xl mx-auto bg-white p-6 rounded shadow">
        <h1 className="text-xl font-bold text-gray-800 mb-2">Upload Documents</h1>
        <p className="text-gray-500">No admission found. Please complete registration first.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-4">Upload Documents</h1>

      {message && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 text-sm">{message}</div>}
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>}

      <div className="space-y-3">
        {requirements.length === 0 ? (
          <div className="bg-white p-4 rounded shadow text-gray-500 text-sm">
            No document requirements configured for your degree.
          </div>
        ) : (
          requirements.map((req) => {
            const uploaded = uploads.find((u) => u.documentTypeId === req.documentTypeId);
            const locked = lockedDocTypeIds.includes(req.documentTypeId);
            const isAadhaarRow = /aadhar|aadhaar/i.test(req.documentType?.name || "");
            return (
              <div key={req.id} className="bg-white p-4 rounded shadow gap-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">
                    {req.documentType?.name || `Document #${req.documentTypeId}`}
                    {req.isRequired && <span className="text-red-500 ml-1">*</span>}
                    {locked && <span className="ml-2 text-xs text-gray-500" title="Locked after admission">🔒</span>}
                  </p>
                  {uploaded && (
                    <p className="text-xs text-green-600 mt-1">
                      Uploaded on {new Date(uploaded.uploadedAt).toLocaleDateString("en-IN")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {uploaded && (
                    <>
                      <a
                        href={`${API_BASE}${uploaded.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        View
                      </a>
                      <a
                        href={`${API_BASE}${uploaded.fileUrl}`}
                        download
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Download
                      </a>
                    </>
                  )}
                  {locked ? (
                    <span className="text-xs text-gray-500 italic">Locked</span>
                  ) : (
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        if (e.target.files[0]) handleUpload(req.documentTypeId, e.target.files[0]);
                      }}
                      className="text-xs"
                    />
                  )}
                </div>
              </div>
              {isAadhaarRow && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                  <label className="text-xs text-gray-600 whitespace-nowrap">Aadhaar Number:</label>
                  <input
                    type="text"
                    value={aadhaar}
                    onChange={(e) => setAadhaar(e.target.value)}
                    onBlur={saveAadhaar}
                    disabled={aadhaarLocked || savingAadhaar}
                    placeholder="Enter Aadhaar number"
                    className={`flex-1 px-2 py-1 border rounded text-xs ${aadhaarLocked ? "bg-gray-100" : ""}`}
                  />
                  {aadhaarLocked && <span className="text-xs text-gray-500" title="Locked after admission">🔒</span>}
                  {savingAadhaar && <span className="text-xs text-gray-500">Saving...</span>}
                </div>
              )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DocumentUploadPage;
